import React, { useMemo, useState } from "react";
import { X, MapPin } from "lucide-react";

/**
 * Create a list, create a category (a list inside a new heading), or move an
 * existing list into / out of a category.
 *
 * Monday 12849996728 (Christian Gravias, 20 Aug 2026): Adriano made a "Social
 * Media Leads" category and wanted a "Preston" one next to it with its own
 * sub-categories ("1br 43 High") underneath. Categories already existed in the
 * data (a list's group heading) but the ONLY way to get one was a CSV import,
 * so there was nothing to click on the page itself. This is that missing UI.
 *
 * A category is normally the heading over its lists, but it does not have to
 * hold anything yet: Christian Gravias (2026-08-22) - "Not every category needs
 * a sub category. Please make it optional that I can just do Category name if I
 * want to." So in "category" mode the sub-category is optional, and a category
 * saved on its own is kept on the agent's view config until a list lands in it.
 *
 * And a heading does not have to stay just a heading: Christian Gravias
 * (2026-08-24) - "I should be able to click onto Test and access the list
 * without having to create another list. I want agents to have the choice of
 * using the heading as a list." Clicking the heading on the page backs it with a
 * list of the same name inside itself, so nothing has to be created here first.
 */
const NEW = "__new__";

export default function ListFormModal({
  mode = "list", // "list" | "category" | "edit"
  list = null, // the list being renamed / moved, in "edit" mode
  categories = [], // existing category titles
  defaultGroup = "", // pre-picked category, e.g. the row the + was clicked on
  onClose,
  onSubmit,
}) {
  const isCategory = mode === "category";
  const isEdit = mode === "edit";
  const startGroup = isEdit ? (list?.group_name || "").trim() : defaultGroup;
  // A pre-picked category that no longer exists (or a brand new one) still has
  // to show as the selection, so it is added to the options rather than dropped.
  const options = useMemo(() => {
    const all = new Set(categories.filter(Boolean));
    if (startGroup) all.add(startGroup);
    return [...all].sort((a, b) => a.localeCompare(b));
  }, [categories, startGroup]);

  const [name, setName] = useState(isEdit ? list?.name || "" : "");
  const [choice, setChoice] = useState(isCategory ? NEW : startGroup);
  const [newGroup, setNewGroup] = useState(isCategory ? defaultGroup : "");
  const [busy, setBusy] = useState(false);

  const group = (choice === NEW ? newGroup : choice).trim();
  // A category needs its own name and nothing else; a list needs its name.
  const ready = isCategory ? Boolean(group) : Boolean(name.trim());

  const title = isEdit
    ? `Rename or move "${list?.name}"`
    : isCategory
      ? "New category"
      : "New list";

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      await onSubmit({ name: name.trim(), group_name: group });
      onClose();
    } catch {
      // The page has already said what went wrong (a name already taken, say),
      // so stay open on what was typed instead of making them start again.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[85] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[420px] max-w-full flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900 truncate">{title}</h3>
          <button onClick={onClose} className="text-gray-500" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          {/* In "category" mode the heading is the point of the dialog, so it is
              asked for first and the list inside it second. */}
          {isCategory && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">
                Category name
              </span>
              <input
                autoFocus
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="e.g. Preston"
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#55c5d0]"
              />
            </label>
          )}

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">
              {isCategory ? "First sub-category inside it (optional)" : "List name"}
            </span>
            <input
              autoFocus={!isCategory}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder={
                isCategory ? "e.g. 1br 43 High - or leave empty" : "e.g. Social Media Leads"
              }
              className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#55c5d0]"
            />
          </label>

          {!isCategory && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400 font-bold">
                Inside a category
              </span>
              <select
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">No category (top row)</option>
                {options.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                <option value={NEW}>+ New category…</option>
              </select>
            </label>
          )}

          {!isCategory && choice === NEW && (
            <input
              autoFocus
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="New category name, e.g. Preston"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#55c5d0]"
            />
          )}

          <p className="text-[11px] text-gray-400 flex items-start gap-1.5">
            <MapPin size={12} className="mt-0.5 shrink-0" />
            {isCategory
              ? name.trim()
                ? `"${group || "The category"}" gets its own row, starting with "${name.trim()}".`
                : `"${group || "The category"}" gets its own row. Click the heading to use it as a list on its own, or add sheets to it with the + at the bottom of the grid.`
              : group
                ? `This list sits under "${group}", with any other lists in that category.`
                : "Lists with no category sit on the top row next to All."}
          </p>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm font-semibold text-gray-600">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!ready || busy}
            className="px-4 py-2 rounded-lg bg-[#55c5d0] text-white text-sm font-bold disabled:opacity-50"
          >
            {busy ? "Saving…" : isEdit ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
